// Adventure Road — window.__qa driver (docs/ARCHITECTURE.md "The __qa contract", docs/DESIGN.md §16).
// HARNESS-OWNED: tools/shot.mjs drives this over Playwright. Render-side dev tooling — may import
// playcanvas, must NEVER write sim state (one-way law); all game mutation flows through the
// QaBackend seam that wave-2 gameRender registers via registerQaBackend().
//
// ══ THE ?qa=1 FLAG CONTRACT — what wave-2 (gameRender/main) MUST implement ══════════════════════
//  1. Boot calls installQa() unconditionally (cheap; inert without the flag). Under ?qa=1 the game
//     boots into QA idle (world 1, no spawns), seeds the sim RNG from ?seed (default 1), mutes the
//     audio master (nodes still run) unless &audio=1, then calls registerQaBackend(backend).
//  2. Under ?qa=1 the game main loop must NOT free-run: qa.ts suspends the rAF loop right after
//     backend registration and stepping yields to __qa.step(). REQUIREMENT: keep the stock AppBase
//     rAF loop (app.start() → app.tick) so the generic suspend/resume here (cancelAnimationFrame on
//     app.frameRequestId / app.tick()) works. A custom rAF loop must instead provide the optional
//     backend.suspendRealtime()/resumeRealtime() hooks, which override the generic path.
//  3. backend.step(dtMs) is SYNCHRONOUS: accumulate dtMs into the fixed-step accumulator → run the
//     due 30 Hz sim ticks → interp → ONE app.render(). No awaits. uTime/ambient clocks advance only
//     by the stepped dtMs while suspended (frozen between steps — the ?dev&freeze discipline).
//  4. backend.frame() MUST synchronously app.render() then capture VIA captureAppPng(app) (exported
//     below) — NOT via the WebGL canvas's own toDataURL. Two hard-won constraints, verified on this
//     machine (RTX 4070, chromium 1223, ANGLE/D3D11): (a) preserveDrawingBuffer is false ⇒ capture
//     is only valid in the SAME task as a render; (b) even in-task, WebGL-canvas.toDataURL races the
//     ANGLE resolve (~60% stale/black frames at 1366x768) — captureAppPng instead gl.readPixels the
//     drawing buffer (a synchronous, spec-guaranteed read) and encodes through a 2D canvas.
//  5. backend.metrics(): simMs/renderMs from your OWN performance.now() frame marks — NEVER
//     stats.frame.updateTime/renderTime (never written in PlayCanvas 2.19.5 — recon/qa-harness §4).
//     programs = graphicsDevice.shaders.length (the zero-shader-growth law gate, perf lane reads it).
//  6. ?scenario=<name> (alias ?scene=) auto-runs before ready flips; ready = backend registered
//     + auto-scenario resolved + first frame rendered. Scenario failures land in __qa.error and
//     ready stays false — the harness fails loudly instead of shooting a broken stage.
//  7. __qa.resumeRealtime() re-enters free-run (perf lane: real fps under rAF); __qa.step() while
//     free-running re-suspends first — manual stepping always owns the clock.
//
// ══ FALLBACK BACKEND (wave-1, active TODAY) ═════════════════════════════════════════════════════
// If no game backend registers within 3 s of installQa() under ?qa=1, a fallback attaches to the
// running pc Application (AppBase.getApplication()): scenario = recorded no-op, step = one
// app.update(dt) + app.render(), frame = render + toDataURL, metrics from app.stats + our own frame
// marks, state = zeros with phase −1. It renders whatever the current (stub) scene shows, so the
// whole capture pipeline is testable before the game exists. The harness injects this module via
// the Vite dev server (`import('/src/game/qa/qa.ts')`) when the page did not install __qa itself.

import { AppBase, Color, Entity, StandardMaterial } from 'playcanvas';

/** Metric snapshot (ARCHITECTURE __qa.metrics + `programs` for the zero-growth perf gate). */
export interface QaMetrics {
  fps: number;
  simMs: number;      // median update/sim CPU ms over the recent ring (own marks, not stats.frame)
  renderMs: number;   // median render-submit CPU ms over the recent ring
  drawCalls: number;
  tris: number;
  enemies: number;
  proj: number;
  heapMB: number;     // usedJSHeapSize; precise only with --enable-precise-memory-info
  programs: number;   // graphicsDevice.shaders.length — must not grow after boot warmup (LAW)
}

/** Run-state snapshot (ARCHITECTURE __qa.state). Fallback reports phase −1 (no game yet). */
export interface QaState {
  phase: number;
  world: number;
  level: number;
  hp: number;
  gold: number;
  kills: number;
}

export interface QaDir { x: number; z: number }

/** The seam wave-2 implements. All methods synchronous except scenario (stage build may await). */
export interface QaBackend {
  /** Build the deterministic stage for `name` seeded with `seed` (DESIGN §16 list). */
  scenario(name: string, seed: number): Promise<void>;
  /** One sim advance of dtMs + ONE app.render, fully synchronous (see contract pt 3). */
  step(dtMs: number): void;
  /** Synchronous render + canvas.toDataURL('image/png') — same-task law (contract pt 4). */
  frame(): string;
  metrics(): QaMetrics;
  state(): QaState;
  /** Scripted movement intent for scenarios (null = release input). */
  input(dir: QaDir | null): void;
  /** 'gold 1000' | 'equip all' | 'trinket <id> n' | 'goto w l' | 'god' (ARCHITECTURE). */
  cheat(cmd: string): void;
  /** Optional overrides when the game does not run the stock AppBase rAF loop (contract pt 2). */
  suspendRealtime?(): void;
  resumeRealtime?(): void;
  /**
   * [AR] Optional teardown, called when THIS backend is replaced by another (in practice: the real
   * game backend winning the race after the fallback already attached). See registerQaBackend.
   */
  dispose?(): void;
}

/** The window.__qa surface the harness talks to (ARCHITECTURE + realtime control for perf). */
export interface QaWindow {
  ready: boolean;
  /** Which backend answered: 'none' until registration, then 'game' | 'fallback'. */
  backend: 'none' | 'game' | 'fallback';
  /** Non-null when install/scenario failed — harness fails loudly with this text. */
  error: string | null;
  scenario(name: string, seed?: number): Promise<void>;
  step(dtMs?: number, frames?: number): void;
  frame(): string;
  metrics(): QaMetrics;
  state(): QaState;
  input(dir: QaDir | null): void;
  cheat(cmd: string): void;
  /** Resume rAF free-run (perf lane). metrics() stays live; step() re-suspends. */
  resumeRealtime(): void;
  suspendRealtime(): void;
}

/** What installQa() hands the caller (wave-2 main keeps this instead of touching window). */
export interface QaHandle {
  /** true when ?qa=1 — the game loop must yield stepping to __qa (header contract). */
  active: boolean;
  /** Scenario/seed requested on the URL (?scenario= | ?scene=, ?seed=), null/1 when absent. */
  urlScenario: string | null;
  urlSeed: number;
  /** true while manual stepping owns the clock (rAF suspended). */
  suspended(): boolean;
  registerBackend(b: QaBackend): void;
}

declare global { interface Window { __qa?: QaWindow } }

// ── module state (one install per document) ─────────────────────────────────────────────────────

const FALLBACK_DELAY_MS = 3000;   // task contract: fallback attaches when no game backend by +3 s
const FALLBACK_RETRY_MS = 500;    // app-not-yet-created retry cadence
const FALLBACK_GIVEUP_MS = 10000; // then surface an error instead of hanging the harness

interface Internal {
  handle: QaHandle | null;
  qa: QaWindow | null;
  backend: QaBackend | null;
  suspended: boolean;
  fallbackTimer: ReturnType<typeof setTimeout> | null;
  completionToken: number; // guards the async completer when a late 'game' replaces 'fallback'
}

const S: Internal = {
  handle: null,
  qa: null,
  backend: null,
  suspended: false,
  fallbackTimer: null,
  completionToken: 0,
};

function app(): AppBase | null {
  return AppBase.getApplication() ?? null;
}

/** Cancel the pending stock-loop rAF (AppBase.tick re-arms each frame; cancelling the pending id
 *  parks the loop). Used unless the backend overrides suspendRealtime. */
function genericSuspend(): void {
  const a = app();
  if (!a) return;
  const id = (a as unknown as { frameRequestId: number | null | undefined }).frameRequestId;
  if (typeof id === 'number') cancelAnimationFrame(id);
  (a as unknown as { frameRequestId: number | null }).frameRequestId = null;
}

/** Re-enter the stock loop: one tick() renders AND re-arms rAF (app-base.js tick → requestAnimationFrame). */
function genericResume(): void {
  const a = app();
  if (!a) return;
  const id = (a as unknown as { frameRequestId: number | null | undefined }).frameRequestId;
  if (typeof id !== 'number') a.tick();
}

function requireBackend(): QaBackend {
  if (!S.backend) throw new Error('__qa: no backend registered yet (ready=false)');
  return S.backend;
}

// ── capture rig: deterministic png of the drawing buffer ────────────────────────────────────────
// WHY NOT canvas.toDataURL on the WebGL canvas: Chromium's WebGL-canvas snapshot path races the
// ANGLE/D3D11 MSAA resolve — in-task captures right after a render intermittently return the
// previous/cleared surface (measured ~60% black at 1366x768 on this machine; roadborne only ever
// captured via page.screenshot, sidestepping it). gl.readPixels of the default framebuffer is a
// synchronous, spec-guaranteed read of the current drawing buffer (implementation resolves MSAA),
// and a plain 2D canvas re-encodes those bytes with no GL involved. Buffers are built once per
// size — no per-frame allocations beyond the returned string (LAW).

interface CaptureRig {
  w: number;
  h: number;
  raw: Uint8Array;            // readPixels target, bottom-up rows
  image: ImageData;           // top-down, handed to the 2D canvas
  canvas2d: HTMLCanvasElement;
  ctx2d: CanvasRenderingContext2D;
}
let capRig: CaptureRig | null = null;

function rigFor(w: number, h: number): CaptureRig {
  if (capRig && capRig.w === w && capRig.h === h) return capRig;
  const canvas2d = capRig?.canvas2d ?? document.createElement('canvas');
  canvas2d.width = w;
  canvas2d.height = h;
  const ctx2d = canvas2d.getContext('2d');
  if (!ctx2d) throw new Error('qa capture: 2d context unavailable');
  capRig = { w, h, raw: new Uint8Array(w * h * 4), image: new ImageData(w, h), canvas2d, ctx2d };
  return capRig;
}

/** Render the app once and return a png dataURL of the drawing buffer (same-task law, pt 4).
 *  Wave-2 backends should implement frame() with this (or equivalent readPixels-based capture). */
export function captureAppPng(a: AppBase): string {
  a.render();
  const device = a.graphicsDevice;
  const gl = (device as unknown as { gl?: WebGL2RenderingContext }).gl;
  const canvas = device.canvas;
  if (!gl) return canvas.toDataURL('image/png'); // non-WebGL device: best effort
  const w = canvas.width;
  const h = canvas.height;
  const rig = rigFor(w, h);
  const prev = gl.getParameter(gl.READ_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
  gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, rig.raw);
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, prev);
  const row = w * 4;
  const dst = rig.image.data;
  for (let y = 0; y < h; y += 1) {
    dst.set(rig.raw.subarray((h - 1 - y) * row, (h - y) * row), y * row); // GL rows are bottom-up
  }
  rig.ctx2d.putImageData(rig.image, 0, 0);
  return rig.canvas2d.toDataURL('image/png');
}

// ── the fallback backend (wave-1: whatever the stub scene shows) ────────────────────────────────

function makeFallbackBackend(a: AppBase): QaBackend {
  // Frame-time rings — preallocated, no per-frame allocation (LAW). Median over the last RING
  // frames; written by BOTH the realtime hooks (frameupdate/framerender/frameend — the honest
  // 2.19.5 timing source, recon/qa-harness §4) and the manual step() path.
  const RING = 120;
  const simRing = new Float64Array(RING);
  const renRing = new Float64Array(RING);
  let ringN = 0;
  let ringI = 0;
  const pushTimes = (simMs: number, renMs: number): void => {
    simRing[ringI] = simMs;
    renRing[ringI] = renMs;
    ringI = (ringI + 1) % RING;
    if (ringN < RING) ringN += 1;
  };
  let tUpdate = 0;
  let tRender = 0;
  a.on('frameupdate', () => { tUpdate = performance.now(); });
  a.on('framerender', () => { tRender = performance.now(); });
  a.on('frameend', () => {
    const tEnd = performance.now();
    pushTimes(tRender - tUpdate, tEnd - tRender);
  });

  const median = (ring: Float64Array, n: number): number => {
    if (n === 0) return 0;
    const c = Array.from(ring.subarray(0, n)).sort((x, y) => x - y); // 1 Hz metrics call — not a hot path
    return c[n >> 1] ?? 0;
  };

  let lastScenario = '';
  let lastSeed = 0;
  let lastInput: QaDir | null = null;

  // ── procedural calibration stage ──────────────────────────────────────────────────────────────
  // The stub scene is an empty void: a near-uniform frame compresses to a sub-5 KB png AND never
  // changes between steps, so the capture/diff/sheet pipeline would be untestable. scenario()
  // therefore builds a tiny deterministic stage — ground plane + a seeded ring of colored spinning
  // boxes under the stub camera/sun. 100% procedural, seeded LCG (never Math.random — LAW),
  // render-side only (no sim state), rebuilt per scenario call; step() advances the spin by the
  // stepped dtMs so consecutive frames are visibly distinct.
  const SPIN_N = 12;
  let stageRoot: Entity | null = null;
  const spinners: Entity[] = [];
  const spinRate: number[] = []; // deg/s per box, seeded

  const mulberry32 = (seed: number): (() => number) => {
    let s = seed >>> 0 || 1;
    return () => {
      s = (s + 0x6d2b79f5) >>> 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const buildStage = (name: string, seed: number): void => {
    stageRoot?.destroy();
    spinners.length = 0;
    spinRate.length = 0;
    const rnd = mulberry32(seed);
    stageRoot = new Entity(`qa-stage:${name}`);
    const groundMat = new StandardMaterial();
    groundMat.diffuse = new Color(0.16, 0.18, 0.2);
    groundMat.update();
    const ground = new Entity('qa-ground');
    ground.addComponent('render', { type: 'plane' });
    if (ground.render) ground.render.material = groundMat;
    ground.setLocalScale(26, 1, 26);
    stageRoot.addChild(ground);
    for (let i = 0; i < SPIN_N; i += 1) {
      const mat = new StandardMaterial();
      mat.diffuse = new Color(0.25 + 0.75 * rnd(), 0.25 + 0.75 * rnd(), 0.25 + 0.75 * rnd());
      mat.update(); // same StandardMaterial feature set every box — one shared program, zero-growth safe
      const box = new Entity(`qa-box-${String(i)}`);
      box.addComponent('render', { type: 'box' });
      if (box.render) box.render.material = mat;
      const ang = (i / SPIN_N) * Math.PI * 2;
      const rad = 2.5 + 4 * rnd();
      const s = 0.5 + 1.2 * rnd();
      box.setLocalScale(s, s * (0.5 + rnd()), s);
      box.setPosition(Math.cos(ang) * rad, 0.4 + 1.6 * rnd(), Math.sin(ang) * rad * 0.7);
      box.setEulerAngles(0, 360 * rnd(), 0);
      stageRoot.addChild(box);
      spinners.push(box);
      spinRate.push(40 + 140 * rnd());
    }
    a.root.addChild(stageRoot);
  };

  const spinStage = (dtMs: number): void => {
    const dtS = dtMs / 1000;
    for (let i = 0; i < spinners.length; i += 1) spinners[i].rotate(0, spinRate[i] * dtS, 0);
  };

  return {
    scenario(name: string, seed: number): Promise<void> {
      lastScenario = name;
      lastSeed = seed;
      // Stub era: build the calibration stage above so captures are non-blank + per-step distinct.
      buildStage(name, seed);
      console.info(`[qa] fallback scenario '${lastScenario}' seed=${String(lastSeed)} (calibration stage, no game backend)`);
      return Promise.resolve();
    },
    step(dtMs: number): void {
      const t0 = performance.now();
      spinStage(Math.max(0, dtMs));
      a.update(Math.max(0, dtMs) / 1000);
      const t1 = performance.now();
      a.render();
      const t2 = performance.now();
      pushTimes(t1 - t0, t2 - t1);
    },
    frame(): string {
      // Same-task law + readPixels capture (header pt 4): captureAppPng re-renders synchronously
      // so the buffer is fresh regardless of which task calls us, then reads it back race-free.
      return captureAppPng(a);
    },
    metrics(): QaMetrics {
      const st = a.stats;
      const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
      return {
        fps: st.frame.fps,                    // stats.updateBasic runs only under rAF ticks —
        drawCalls: st.drawCalls.total,        // live in realtime/perf mode, stale under manual stepping
        tris: st.frame.triangles,             // populated by the dev (dbg) build's updateDetailed
        simMs: median(simRing, ringN),
        renderMs: median(renRing, ringN),
        enemies: 0,
        proj: 0,
        heapMB: mem ? mem.usedJSHeapSize / (1024 * 1024) : 0,
        programs: a.graphicsDevice.shaders.length,
      };
    },
    state(): QaState {
      // phase −1 flags "fallback — no game sim behind this" to the harness/_meta.
      return { phase: -1, world: 0, level: 0, hp: 0, gold: 0, kills: 0 };
    },
    input(dir: QaDir | null): void { lastInput = dir; },
    cheat(cmd: string): void {
      console.info(`[qa] fallback cheat ignored: '${cmd}' (scenario='${lastScenario}', input=${JSON.stringify(lastInput)})`);
    },
    /**
     * [AR] Tear the calibration stage back out. THIS WAS A REAL, SILENT CAPTURE CORRUPTER.
     *
     * The fallback attaches 3 s after installQa() if the game has not registered yet, and its
     * scenario() builds a debug stage — a 26x26 dark ground plane and 12 saturated spinning boxes.
     * When the game backend then registers LATE (a cold vite server transforming the module graph
     * for the first time comfortably exceeds 3 s), registerQaBackend replaced the backend but left
     * those entities parented to app.root. Result: `__qa.backend` reads a reassuring 'game', the
     * probe reports ready + zero errors + "frame has image content", and every pixel it measures is
     * of a scene with a black slab and a dozen coloured cubes sitting on top of the road.
     * Caught by eyeballing a boot PNG whose mean had dropped from [161,159,123] to [69,75,48] —
     * i.e. by looking at the image, not at the report that said everything was fine.
     */
    dispose(): void {
      stageRoot?.destroy();
      stageRoot = null;
      spinners.length = 0;
      spinRate.length = 0;
    },
  };
}

// ── registration + completion ───────────────────────────────────────────────────────────────────

function suspendRealtime(): void {
  const b = S.backend;
  if (b?.suspendRealtime) b.suspendRealtime();
  else genericSuspend();
  S.suspended = true;
}

function resumeRealtime(): void {
  const b = S.backend;
  if (b?.resumeRealtime) b.resumeRealtime();
  else genericResume();
  S.suspended = false;
}

async function runScenario(name: string, seed: number): Promise<void> {
  const b = requireBackend();
  const q = S.qa;
  if (S.handle?.active) suspendRealtime();
  await b.scenario(name, seed);
  b.step(0); // first frame of the loaded stage rendered before anyone captures
  if (q) q.error = null;
}

/** Backend arrival → (suspend under ?qa=1) → auto-run URL scenario → first frame → ready. */
function completeInstall(kind: 'game' | 'fallback'): void {
  const q = S.qa;
  const h = S.handle;
  if (!q || !h) return;
  const token = (S.completionToken += 1);
  q.backend = kind;
  q.ready = false;
  void (async () => {
    try {
      if (h.active) suspendRealtime();
      if (h.urlScenario) await runScenario(h.urlScenario, h.urlSeed);
      else if (S.backend) S.backend.step(0); // QA idle: still render one frame so capture ≠ blank
      if (S.completionToken === token) q.ready = true;
    } catch (e) {
      if (S.completionToken === token) {
        q.error = `qa ${kind} install failed: ${e instanceof Error ? e.message : String(e)}`;
        console.error('[qa]', q.error);
      }
    }
  })();
}

/** Wave-2 entry: hand the game backend to __qa. A late 'game' registration REPLACES the fallback
 *  (slow world build racing the 3 s timer) and re-completes readiness. */
export function registerQaBackend(b: QaBackend): void {
  if (S.fallbackTimer !== null) { clearTimeout(S.fallbackTimer); S.fallbackTimer = null; }
  // [AR] the outgoing backend gets to clean up after itself before it is dropped — otherwise a
  // fallback that already built its calibration stage leaves it in the scene forever (see dispose()).
  const prev = S.backend;
  if (prev && prev !== b && prev.dispose) {
    try { prev.dispose(); } catch (e) { console.warn('[qa] previous backend dispose failed', e); }
  }
  S.backend = b;
  completeInstall('game');
}

function attachFallback(deadline: number): void {
  if (S.backend) return; // a game backend won the race
  const a = app();
  if (!a) {
    if (performance.now() < deadline) {
      S.fallbackTimer = setTimeout(() => { attachFallback(deadline); }, FALLBACK_RETRY_MS);
    } else if (S.qa) {
      S.qa.error = 'qa fallback: no PlayCanvas Application found (AppBase.getApplication() empty)';
      console.error('[qa]', S.qa.error);
    }
    return;
  }
  S.backend = makeFallbackBackend(a);
  completeInstall('fallback');
}

// ── install ─────────────────────────────────────────────────────────────────────────────────────

/** Installs window.__qa immediately (ready=false) and returns the handle. Idempotent — the harness
 *  injects this module when the page did not, so double-install must be a no-op. */
export function installQa(): QaHandle {
  if (S.handle) return S.handle;

  const params = new URLSearchParams(window.location.search);
  const active = params.get('qa') === '1';
  const urlScenario = params.get('scenario') ?? params.get('scene');
  const seedRaw = Number(params.get('seed'));
  const urlSeed = Number.isFinite(seedRaw) && seedRaw !== 0 ? seedRaw : 1;

  const handle: QaHandle = {
    active,
    urlScenario,
    urlSeed,
    suspended: () => S.suspended,
    registerBackend: registerQaBackend,
  };

  const qa: QaWindow = {
    ready: false,
    backend: 'none',
    error: null,
    scenario(name: string, seed: number = urlSeed): Promise<void> {
      return runScenario(name, seed);
    },
    step(dtMs = 1000 / 60, frames = 1): void {
      const b = requireBackend();
      if (!S.suspended) suspendRealtime(); // manual stepping owns the clock (header pt 7)
      for (let i = 0; i < frames; i += 1) b.step(dtMs);
    },
    frame(): string { return requireBackend().frame(); },
    metrics(): QaMetrics { return requireBackend().metrics(); },
    state(): QaState { return requireBackend().state(); },
    input(dir: QaDir | null): void { requireBackend().input(dir); },
    cheat(cmd: string): void { requireBackend().cheat(cmd); },
    resumeRealtime,
    suspendRealtime,
  };

  S.handle = handle;
  S.qa = qa;
  window.__qa = qa;

  if (active && S.backend === null) {
    S.fallbackTimer = setTimeout(() => {
      attachFallback(performance.now() + (FALLBACK_GIVEUP_MS - FALLBACK_DELAY_MS));
    }, FALLBACK_DELAY_MS);
  }

  return handle;
}
